import { Utility } from "../../utils";
import { ICountryInfo } from "../country";

export interface Address extends ICountryInfo {
    address1?: string;
    address2?: string;
    address3?: string;
    city?: string;
    state?: string;
    region?: string;
    postalCode?: string;
}

export type CountryDisplayType = "iso" | "iso3" | "name";

export class AddressHelper {
    /**
     * Returns a formatted display of country (full name, iso code 2, iso code 3)
     * @param a address object
     * @param countryDisplayType How to display the country
     */
    public static countryDisplay(a: Address | undefined | null, countryDisplayType: CountryDisplayType) {
        if (!a)
            return "";

        let country;
        switch (countryDisplayType) {
            case "iso":
                country = a.countryCode;
                break;
            case "iso3":
                country = a.countryCode3;
                break;
            case "name":
            default:
                country = a.country;
                break;
        }

        return country;
    }

    /**
     * Returns a formatted display of an address object
     * @param a Address object
     * @param [countryDisplayType] Whether or not to use the country's full name or code. Defaults to true.
     * @param [nlChar] New line character. Defaults to "<br />"
     */
    public static displayFormatted(a: Address | undefined | null, countryDisplayType: CountryDisplayType = "name", nlChar: string = "<br />"): string {
        if (!a)
            return "";

        const statePostal = Utility.joinSkipEmpty([a.state, a.postalCode], " ");
        const cityStateZip = Utility.joinSkipEmpty([a.city, statePostal], ", ");
        const country = AddressHelper.countryDisplay(a, countryDisplayType);

        return Utility.joinSkipEmpty([
            a.address1,
            a.address2,
            a.address3,
            cityStateZip,
            a.region,
            country,
        ], nlChar);
    }

    /**
     * Returns an inline formatted display of an address object
     * @param a Address object
     * @param [countryDisplayType] Whether or not to use the country's full name or code. Defaults to true.
     * @param [delim] delimiter character. Defaults to ", "
     */
    public static displayInline(a?: Address, countryDisplayType: CountryDisplayType = "name", delim: string = ", "): string {
        if (!a)
            return "";

        const statePostal = Utility.joinSkipEmpty([a.state, a.postalCode], " ");
        const cityStateZip = Utility.joinSkipEmpty([a.city, statePostal, a.region], ", ");
        const country = AddressHelper.countryDisplay(a, countryDisplayType);

        return Utility.joinSkipEmpty([
            a.address1,
            a.address2,
            a.address3,
            cityStateZip,
            country,
        ], delim);
    }

    /**
     * Returns an inline formatted display of an address object
     * @param a Address object
     * @param [countryDisplayType] Whether or not to use the country's full name or code. Defaults to true.
     * @param [delim] delimiter character. Defaults to ", "
     */
    public static displayCityStateInline(a: Address | undefined | null, countryDisplayType: CountryDisplayType, displayUS: boolean): string {
        if (!a)
            return "";

        const cityState = Utility.joinSkipEmpty([a.city, a.state, a.region], ", ");
        const country = AddressHelper.countryDisplay(a, countryDisplayType);

        return Utility.joinSkipEmpty([
            cityState,
            !displayUS && a.countryCode === "US" ? undefined : country,
        ], ", ");
    }
}



// WEBPACK FOOTER //
// ./src/models/generic/Address.ts